import React, { useEffect, useState } from 'react';
import { ReactComponent as DocumentIcon } from 'app/assets/Icons/Document.svg';
import { CollapsibleSection } from 'app/components/Sections/AskYourDocs/Sidebar/CollapsibleSection/CollapsibleSection';
import {
  createDocumentFolder,
  deleteDocument,
  deleteDocumentFolder,
  downloadDocument,
  getHistory,
  moveOrCopyDocument,
  renameDocument,
  renameDocumentFolder,
  translateDocument,
  uploadDocuments
} from 'integration';
import config from 'config';
import { useNavigate } from 'react-router-dom';
import { DocumentFolder } from '../DocumentFolder/DocumentFolder';
import { IDocumentList, IDocumentListProps } from './DocumentList.interface';
import { DocumentItem } from '../DocumentItem/DocumentItem';
import {
  getBusinessArea,
  getSelectedDocuments,
  getService,
  useAppSelector
} from 'app/redux';
import {
  handleFileValidation,
  displayToastError,
  hasSpecialCharacters
} from 'app/utils';
import { SERVICE_NAMES } from 'app/utils/consts';
import { Language } from 'app/components/LanguageSelector';
import { LoadingSpinner } from 'app/components';

const buildTitles = (files: File[]): Array<{ title: string }> => {
  const result: Array<{ title: string }> = [];
  for (const file of files) {
    result.push({ title: file.name.replace(/\.[^/.]+$/, '') });
  }
  return result;
};

const removeDoc = (
  docs: IDocumentList['documents'],
  folder: string,
  title: string
): IDocumentList['documents'] => {
  if (!docs[folder]) return docs;
  return {
    ...docs,
    [folder]: {
      ...docs[folder],
      titles: docs[folder].titles.filter((d) => d.title !== title)
    }
  };
};

const addDoc = (
  docs: IDocumentList['documents'],
  folder: string,
  title: string
): IDocumentList['documents'] => {
  if (!docs[folder]) return docs;
  return {
    ...docs,
    [folder]: { ...docs[folder], titles: [...docs[folder].titles, { title }] }
  };
};

const renameDoc = (
  docs: IDocumentList['documents'],
  folder: string,
  oldTitle: string,
  newTitle: string
): IDocumentList['documents'] => {
  if (!docs[folder]) return docs;
  return {
    ...docs,
    [folder]: {
      ...docs[folder],
      titles: docs[folder].titles.map((d) =>
        d.title === oldTitle ? { ...d, title: newTitle } : d
      )
    }
  };
};

export const DocumentList: React.FC<IDocumentListProps> = ({
  documentData,
  onRefreshChatList
}) => {
  const navigate = useNavigate();
  const [documentList, setDocumentList] = useState<IDocumentList>({
    documents: {}
  });
  const [editingDocumentPath, setEditingDocumentPath] = useState<string | null>(
    null
  );
  const [editingFolderName, setEditingFolderName] = useState<string | null>(
    null
  );
  const selectedDocuments = useAppSelector(getSelectedDocuments);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [collapseTrigger, setCollapseTrigger] = useState(0);
  const currentService =
    useAppSelector(getService) ?? SERVICE_NAMES.ASK_YOUR_DOCS;
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  useEffect(() => {
    if (documentData && !isLoading) {
      setDocumentList((prevState) => {
        if (Object.keys(prevState.documents).length === 0) {
          return documentData;
        }
        return prevState;
      });
    }
  }, [documentData]);

  const handleGetDocuments = (): void => {
    setIsLoading(true);
    getHistory(currentBusinessArea, currentService, 'documents')
      .then((response) => {
        console.log(response);
        setDocumentList(response);
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteDocument = (documentPath: string): void => {
    setIsLoading(true);
    deleteDocument(currentBusinessArea, currentService, documentPath)
      .then(() => {
        const [folderName, documentName] = documentPath.split('/');
        setDocumentList((prev) => ({
          documents: removeDoc(prev.documents, folderName, documentName)
        }));
        onRefreshChatList();
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRenameDocument = (
    documentPath: string,
    newDocumentName: string
  ): void => {
    if (hasSpecialCharacters(newDocumentName)) {
      displayToastError(
        'Document name can not contain special characters',
        'Invalid name'
      );
      return;
    }

    setIsLoading(true);
    const [folderName, oldDocumentName] = documentPath.split('/');

    const payload = {
      folderName,
      documentTitle: oldDocumentName,
      newDocTitle: newDocumentName
    };

    renameDocument(currentBusinessArea, currentService, payload)
      .then(() => {
        setDocumentList((prev) => ({
          documents: renameDoc(
            prev.documents,
            folderName,
            oldDocumentName,
            newDocumentName
          )
        }));
        setEditingDocumentPath(null);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const startEditingDocument = (documentPath: string): void => {
    setEditingDocumentPath(documentPath);
  };

  const cancelEditingDocument = (): void => {
    setEditingDocumentPath(null);
  };

  const handleCreateFolder = (folderName: string): void => {
    setIsLoading(true);
    createDocumentFolder(currentBusinessArea, currentService, folderName)
      .then(() => {
        setDocumentList((prevDocumentList) => ({
          documents: {
            ...prevDocumentList.documents,
            [folderName]: { titles: [] }
          }
        }));
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRenameFolder = (
    folderName: string,
    newFolderName: string
  ): void => {
    if (hasSpecialCharacters(newFolderName)) {
      displayToastError(
        'Folder name can not contain special characters',
        'Invalid name'
      );
      return;
    }

    setIsLoading(true);
    renameDocumentFolder(
      currentBusinessArea,
      currentService,
      folderName,
      newFolderName
    )
      .then(() => {
        setDocumentList((prevDocumentList) => {
          const newDocuments = { ...prevDocumentList.documents };

          if (newDocuments[folderName]) {
            newDocuments[newFolderName] = newDocuments[folderName];
            const { [folderName]: movedFolder, ...remainingDocuments } =
              prevDocumentList.documents;
            if (movedFolder) {
              return {
                documents: {
                  ...remainingDocuments,
                  [newFolderName]: movedFolder
                }
              };
            }
            return { documents: prevDocumentList.documents };
          }

          return { documents: newDocuments };
        });

        setEditingFolderName(null);
      })
      .catch((error) => {
        setEditingFolderName(null);
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteFolder = (folderName: string): void => {
    setIsLoading(true);
    deleteDocumentFolder(currentBusinessArea, currentService, folderName)
      .then(() => {
        setDocumentList((prevDocumentList) => {
          const newDocuments = { ...prevDocumentList.documents };
          const { [folderName]: removedFolder, ...remainingDocuments } =
            newDocuments;
          return { documents: remainingDocuments };
        });

        onRefreshChatList();
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const startEditingFolder = (folderName: string): void => {
    setEditingFolderName(folderName);
  };

  const cancelEditingFolder = (): void => {
    setEditingFolderName(null);
  };

  const handleMoveOrCopyDocument = (
    documentPath: string,
    targetFolderName: string,
    copy: boolean
  ): void => {
    setIsLoading(true);
    const [folderName, documentTitle] = documentPath.split('/');

    const payload = {
      folderName,
      documentTitle,
      newFolderName: targetFolderName,
      copy
    };

    moveOrCopyDocument(currentBusinessArea, currentService, payload)
      .then(() => {
        setDocumentList((prev) => {
          let docs = { ...prev.documents };
          docs = copy
            ? addDoc(docs, targetFolderName, documentTitle)
            : addDoc(
                removeDoc(docs, folderName, documentTitle),
                targetFolderName,
                documentTitle
              );
          return { documents: docs };
        });
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteSelectedDocument = (): void => {
    for (const documentPath of selectedDocuments) {
      handleDeleteDocument(documentPath);
    }
  };

  const handleUploadDocument = (files: File[], folderName: string): void => {
    setIsUploadingDocuments(true);
    const invalidFiles: string[] = [];
    for (const file of files) {
      const validation = handleFileValidation(
        file,
        SERVICE_NAMES.ASK_YOUR_DOCS
      );
      if (!validation.valid && validation.error)
        invalidFiles.push(`${file.name}: ${validation.error}`);
    }

    if (invalidFiles.length > 0) {
      displayToastError(invalidFiles.join('\n'), 'File validation failed');
      setIsUploadingDocuments(false);
      return;
    }

    const formData = new FormData();
    formData.append('folder', folderName);
    for (const file of files) {
      formData.append('documents', file);
    }

    uploadDocuments(currentBusinessArea, currentService, formData)
      .then(() => {
        setDocumentList((prevDocumentList) => {
          const newDocuments = { ...prevDocumentList.documents };

          const newTitles = buildTitles(files);

          if (!newDocuments[folderName]) {
            newDocuments[folderName] = { titles: [] };
          }

          newDocuments[folderName] = {
            ...newDocuments[folderName],
            titles: [...newDocuments[folderName].titles, ...newTitles]
          };

          return { documents: newDocuments };
        });
      })
      .catch((error) => {
        console.error('Error uploading document:', error);
      })
      .finally(() => {
        setIsUploadingDocuments(false);
      });
  };

  const handleCollapseFolders = (): void => {
    setCollapseTrigger((prev) => prev + 1);
  };

  const handleDownloadDocument = (documentPath: string): void => {
    setIsLoading(true);

    downloadDocument(currentBusinessArea, currentService, documentPath, false)
      .then((response) => {
        const blob = new Blob([Uint8Array.from(response.data)], {
          type: 'application/pdf'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${documentPath}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error downloading document:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTranslateDocument = (
    documentPath: string,
    language: Language
  ): void => {
    setIsLoading(true);

    const payload = {
      documentPath,
      selectedLanguage: {
        name: language.name,
        code: language.code
      }
    };
    const translatedDocumentPath = `${documentPath}_${language.name}`;

    translateDocument(currentBusinessArea, currentService, payload)
      .then(() => {
        handleDownloadDocument(translatedDocumentPath);
        handleGetDocuments();
      })
      .catch((error) => {
        console.error('Error translating document:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const documentActions = {
    onEdit: startEditingDocument,
    onDelete: handleDeleteDocument,
    onRename: handleRenameDocument,
    onCancelEditing: cancelEditingDocument,
    onDownload: handleDownloadDocument,
    onTranslate: handleTranslateDocument,
    onMoveOrCopy: handleMoveOrCopyDocument
  };

  return (
    <CollapsibleSection
      title="Documents"
      icon={<DocumentIcon />}
      showOptionsButton
      onCreateFolder={handleCreateFolder}
      onDeleteDocument={handleDeleteSelectedDocument}
      onUploadDocument={(files) => handleUploadDocument(files, '__ROOT__')}
      onCollapseFolders={handleCollapseFolders}
      isLoading={isLoading}
    >
      {Object.entries(documentList.documents)
        .sort(([folderNameA], [folderNameB]) => {
          if (folderNameA === '__ROOT__') return -1;
          if (folderNameB === '__ROOT__') return 1;
          return 0;
        })
        .map(([folderName, folder]) => {
          if (folderName === '__ROOT__') {
            return folder.titles.map((document) => {
              const documentPath = `${folderName}/${document.title}`;
              return (
                <DocumentItem
                  key={document.title}
                  documentName={document.title}
                  documentPath={documentPath}
                  isEditing={editingDocumentPath === documentPath}
                  documentActions={documentActions}
                  maxDisplayLength={25}
                />
              );
            });
          } else {
            const folderActions = {
              onEdit: startEditingFolder,
              onDelete: handleDeleteFolder,
              onRename: handleRenameFolder,
              onCancelEditing: cancelEditingFolder,
              onUpload: (files: File[]) =>
                handleUploadDocument(files, folderName)
            };

            return (
              <DocumentFolder
                key={folderName}
                folderName={folderName}
                documents={folder.titles.map((doc) => doc.title)}
                collapseTrigger={collapseTrigger}
                editingDocumentPath={editingDocumentPath}
                editingFolderName={editingFolderName}
                documentActions={documentActions}
                folderActions={folderActions}
              />
            );
          }
        })}
      {isUploadingDocuments && <LoadingSpinner />}
    </CollapsibleSection>
  );
};
